'use client';

import { FC } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import toast, { Toaster as HotToaster, ToastBar } from 'react-hot-toast';

import { ErrorNotification } from './errorNotification';
import { InfoCircleIcon } from '../../icons/infoCircle';

// Type definition for our enhanced toast
const enhancedToast = toast as typeof toast & {
  info(message: string, options?: any): string;
};

export { enhancedToast as toast };

export const Toaster: FC = () => {
  const { breakpoints, palette, shadows, typography } = useTheme();
  const sm = useMediaQuery(breakpoints.up('sm'));

  enhancedToast.info = (message: string, options = {}) => {
    return toast(message, {
      icon: <InfoCircleIcon sx={{ color: palette.info.main }} />,
      style: {
        background: palette.info.light,
        color: palette.info.dark,
        ...options,
      },
      ...options,
    });
  };

  return (
    <HotToaster
      containerStyle={{
        bottom: 40,
        left: 40,
        right: 40,
        top: 40,
      }}
      position="bottom-right"
      toastOptions={{
        duration: 2000,
        error: {
          icon: <ErrorIcon />,
          style: {
            background: palette.error.main,
            color: palette.secondary.darkest,
          },
        },
        style: {
          backdropFilter: 'blur(6px)',
          background: alpha(palette.neutral[900], 0.8),
          boxShadow: shadows[16],
          color: palette.secondary.darkest,
          ...typography.body1,
          maxWidth: sm ? 500 : undefined,
        } as any,
        success: {
          icon: <CheckCircleOutlineIcon sx={{ color: palette.success.main }} />,
          style: {
            background: palette.success.light,
            color: palette.secondary.darkest,
          },
        },
      }}
    >
      {(t) => (
        <ToastBar data-testid="toast-bar" toast={t}>
          {({ icon, message }) => {
            const errorMessage = typeof t.message === 'function' ? t.message(t) : t.message;

            // We need to identify if we should show error message with "Show more" button
            const errorMessageLines =
              typeof errorMessage === 'string' ? errorMessage.split('\n') : [];
            const errorMessageMultiLine =
              // By default "ErrorNotification" shows first 2 lines
              errorMessageLines.length > 2 ||
              (errorMessageLines.length === 2 && errorMessageLines[1].length > 50);

            return (
              <>
                {icon}
                {t.type === 'error' && errorMessageMultiLine ? (
                  <Box padding={1}>
                    <ErrorNotification error={errorMessage} />
                  </Box>
                ) : (
                  message
                )}
                {t.type !== 'loading' && (
                  <IconButton
                    data-testid="toast-close-button"
                    onClick={() => toast.dismiss(t.id)}
                    sx={{ color: palette.secondary.darkest }}
                  >
                    <CloseIcon color="inherit" />
                  </IconButton>
                )}
              </>
            );
          }}
        </ToastBar>
      )}
    </HotToaster>
  );
};
